// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/extraSelection.ts
// 🔧 PURPOSE: L'aritmetica e le regole di UNA card della sezione Extra —
//             quantità, prezzo d'anteprima, "si può comprare?", cosa finisce
//             sul foglio di pagamento.
//
//             Stavano dentro Extras.tsx, dove servivano a un solo schermo.
//             Adesso gli stessi servizi si comprano anche dalle schermate
//             dedicate (/extras/:serviceId), e due copie della stessa formula
//             sono due prezzi che prima o poi divergono: quello della lista e
//             quello della schermata singola. Qui c'è una copia sola, pura e
//             senza React, che entrambe chiamano.
//
//             Il server resta l'unico a decidere quanto si paga (riprezza
//             sempre da capo): questi numeri servono a non far comparire una
//             sorpresa sul foglio Stripe.
// ============================================

import type { AppExtra, ExtraCheckoutDetails, ExtrasCatalog, PurchasedExtra, StayPhase } from './guestApi';
import { canBuyTransfer, quoteTransfer, validateTransferSelection, type TransferSelection, type TransferDateLimits } from './transferPricing';

/** Tutto quello che l'ospite ha scelto su una card, prima di pagare. */
export type ExtraSelection = ExtraCheckoutDetails & { quantity?: number };

/** Una bottiglia del catalogo, come arriva dal server. */
export type WineVariant = NonNullable<AppExtra['options']['wineVariants']>[number];
/** Una confezione di "Caffè & Brioche". */
export type BundleVariant = NonNullable<AppExtra['options']['bundleVariants']>[number];
/** Un articolo dei servizi "scegli un articolo" (carta, igiene, kit, biancheria). */
export type ItemVariant = NonNullable<AppExtra['options']['itemVariants']>[number];

/** Unità massime per acquisto: lo stesso tetto del server. */
export const MAX_ITEM_QUANTITY = 10;

/** I prezzi "a persona / a notte": la quantità la decide la prenotazione. */
export const STAY_PRICED_TYPES = ['per_person', 'per_night', 'per_person_night'];

export const isStayPriced = (priceType: string): boolean =>
  STAY_PRICED_TYPES.indexOf(priceType) >= 0;

/**
 * Il prezzo VERO di una bottiglia: listino meno lo sconto della variante.
 * È la formula del server (extra-pricing.ts, ramo vino) e quella del tablet,
 * copiata qui perché l'anteprima non possa dire un numero e il pagamento un
 * altro.
 */
export const wineUnitPrice = (v: WineVariant): number =>
  v.discountPercentage && v.discountPercentage > 0
    ? Math.round(v.price * (1 - v.discountPercentage / 100) * 100) / 100
    : v.price;

/**
 * Il prezzo VERO di un articolo: listino meno lo sconto dell'articolo (per
 * esempio l'acqua a −10%). Stessa formula delle bottiglie e del server
 * (extra-pricing.ts, itemUnitPrice): il prezzo visto è il prezzo pagato.
 */
export const itemUnitPrice = (v: Pick<ItemVariant, 'price' | 'discountPercentage'>): number =>
  v.discountPercentage && v.discountPercentage > 0
    ? Math.round(v.price * (1 - v.discountPercentage / 100) * 100) / 100
    : v.price;

/** Quante bottiglie di questa variante sono nel carrello della card. */
export const wineQtyOf = (selection: ExtraSelection, id: string): number => {
  const found = (selection.wineSelections || []).filter((w) => w.id === id)[0];
  return found ? found.qty : 0;
};

/** Bottiglie totali scelte: è la quantità della riga d'acquisto. */
export const wineBottles = (selection: ExtraSelection): number =>
  (selection.wineSelections || []).reduce((sum, w) => sum + (Number(w.qty) || 0), 0);

/**
 * Le bottiglie scelte in una riga leggibile ("2× Primitivo, 1× Rosato"): la
 * card del vino porta il nome del servizio, e sul foglio di pagamento senza
 * questo elenco l'ospite non saprebbe più cosa sta comprando.
 */
export const wineSummary = (
  variants: WineVariant[] | undefined,
  selection: ExtraSelection,
): string => {
  const parts: string[] = [];
  (variants || []).forEach((v) => {
    const qty = wineQtyOf(selection, v.id);
    if (qty > 0) parts.push(`${qty}× ${v.name}`);
  });
  return parts.join(', ');
};

/** Le bottiglie in vendita per questo servizio (null se non è un vino). */
export const wineVariantsOf = (item: AppExtra): WineVariant[] | null => {
  const list = item.options.wineVariants;
  return list && list.length > 0 ? list : null;
};

/** Le confezioni in vendita per questo servizio (null se non ne ha). */
export const bundleVariantsOf = (item: AppExtra): BundleVariant[] | null => {
  const list = item.options.bundleVariants;
  return list && list.length > 0 ? list : null;
};

/** La confezione attualmente scelta (undefined se nessuna). */
export const selectedBundle = (
  item: AppExtra,
  selection: ExtraSelection,
): BundleVariant | undefined =>
  (item.options.bundleVariants || []).filter((b) => b.id === selection.bundleVariant)[0];

/**
 * Le parole della confezione. `format`, se c'è, scrive i pezzi con i plurali
 * della lingua ("1 coffee", "7 coffees", "5 kaw"); senza, le due parole
 * fisse di sempre.
 */
export interface BundleLabels {
  coffee: string;
  brioche: string;
  format?: (coffeeQty: number, briocheQty: number) => string;
}

/**
 * I pezzi di una confezione SENZA le parti a zero: "7 caffè", "4 brioche",
 * "5 caffè + 5 brioche" — mai "+ 0 brioche" o "0 caffè +". È la stessa regola
 * del riepilogo che il server scrive in ricevuta (lib/bundle-label del
 * channel manager).
 */
export const bundleLabel = (
  bundle: Pick<BundleVariant, 'coffeeQty' | 'briocheQty'>,
  labels: BundleLabels,
): string => {
  const coffee = Math.max(0, Math.trunc(Number(bundle.coffeeQty) || 0));
  const brioche = Math.max(0, Math.trunc(Number(bundle.briocheQty) || 0));
  if (labels.format) return labels.format(coffee, brioche);
  const parts: string[] = [];
  if (coffee > 0) parts.push(`${coffee} ${labels.coffee}`);
  if (brioche > 0) parts.push(`${brioche} ${labels.brioche}`);
  return parts.join(' + ');
};

/**
 * I pezzi con le traduzioni plurali dell'app (extras.bundle.coffeeCount /
 * briocheCount): "1 coffee + 1 croissant", "7 kaw", "4个可颂".
 */
export function bundlePiecesText(
  t: (key: string, options?: Record<string, unknown>) => string,
  coffeeQty: number,
  briocheQty: number,
): string {
  const parts: string[] = [];
  if (coffeeQty > 0) parts.push(t('extras.bundle.coffeeCount', { count: coffeeQty }));
  if (briocheQty > 0) parts.push(t('extras.bundle.briocheCount', { count: briocheQty }));
  return parts.join(' + ');
}

/**
 * La confezione in una riga leggibile ("5 caffè + 5 brioche", "7 caffè"): sul
 * foglio di pagamento "Caffè & Brioche" da solo non dice quanti pezzi si
 * stanno comprando, ed è esattamente il riepilogo che il server scrive in
 * ricevuta (extra-pricing.ts, ramo bundle).
 */
export const bundleSummary = (
  bundle: BundleVariant | undefined,
  labels: BundleLabels,
  packs?: number,
): string => {
  if (!bundle) return '';
  const base = bundleLabel(bundle, labels);
  return packs && packs > 1 ? `${packs}× ${base}` : base;
};

/**
 * Gli articoli in vendita per questo servizio (null se non ne ha). Il "più
 * richiesto" va per primo; gli altri restano nell'ordine del catalogo.
 */
export const itemVariantsOf = (item: AppExtra): ItemVariant[] | null => {
  const list = item.options.itemVariants;
  if (!list || list.length === 0) return null;
  const popular = list.filter((v) => v.highlight === 'popular');
  if (popular.length === 0) return list;
  return popular.concat(list.filter((v) => v.highlight !== 'popular'));
};

/**
 * Il prezzo "pieno" da barrare (somma dei pezzi singoli), solo quando è
 * davvero più alto del prezzo: il server lo manda già così, ma un numero
 * barrato più basso del prezzo sarebbe una bugia in vetrina.
 */
export const compareAtOf = (v: { price: number; compareAtPrice?: number }): number | null =>
  typeof v.compareAtPrice === 'number' && v.price > 0 && v.compareAtPrice > v.price
    ? v.compareAtPrice
    : null;

/**
 * Il prezzo pieno della scelta attuale (articolo o confezione × unità), da
 * barrare accanto al totale. null quando non c'è niente da risparmiare.
 */
export function compareAtTotal(item: AppExtra, selection: ExtraSelection): number | null {
  // Le stesse unità di previewPrice, così barrato e totale parlano della stessa cosa.
  if (bundleVariantsOf(item)) {
    const chosen = selectedBundle(item, selection);
    const full = chosen ? compareAtOf(chosen) : null;
    const packs = Math.max(1, Number(selection.quantity) || 1);
    return full === null ? null : Math.round(full * packs * 100) / 100;
  }
  if (itemVariantsOf(item)) {
    const chosen = selectedItem(item, selection);
    // Scontato: si barra il listino (come per le bottiglie); altrimenti il
    // prezzo dei pezzi presi singolarmente, se c'è.
    const full = !chosen ? null : itemUnitPrice(chosen) < chosen.price ? chosen.price : compareAtOf(chosen);
    const units = Math.max(1, Math.min(itemMaxQuantity(chosen), Number(selection.quantity) || 1));
    return full === null ? null : Math.round(full * units * 100) / 100;
  }
  return null;
}

/** L'articolo attualmente scelto (undefined se nessuno). */
export const selectedItem = (
  item: AppExtra,
  selection: ExtraSelection,
): ItemVariant | undefined =>
  (item.options.itemVariants || []).filter((v) => v.id === selection.itemVariant)[0];

/**
 * L'articolo più economico del servizio: è il "da 2,00 €" delle schede e dei
 * riquadri, perché questi servizi a listino costano 0 — il prezzo sta tutto
 * negli articoli.
 */
export const cheapestItemPrice = (item: AppExtra): number | null => {
  let min: number | null = null;
  (item.options.itemVariants || []).forEach((v) => {
    const price = itemUnitPrice(v);
    if (price > 0 && (min === null || price < min)) min = price;
  });
  return min;
};

/**
 * La bottiglia scelta per un kit che la prevede (il Cestino della Nonna):
 * quella dell'ospite se è fra le scelte di adesso, altrimenti la
 * predefinita del catalogo. undefined per gli altri articoli.
 */
export function chosenKitDrink(
  chosen: Pick<ItemVariant, 'drinkChoice'> | undefined,
  selection: Pick<ExtraSelection, 'kitDrink'>,
): { id: string; kind: string; name: string } | undefined {
  const choice = chosen && chosen.drinkChoice;
  if (!choice || !choice.options || choice.options.length === 0) return undefined;
  const byId = (id?: string) => choice.options.filter((o) => o.id === id)[0];
  return byId(selection.kitDrink) || byId(choice.default) || choice.options[0];
}

/**
 * L'articolo in una riga leggibile ("Carta igienica · 4 rotoli × 2"): sul
 * foglio di pagamento e nel carrello il nome del servizio ("Carta igienica e
 * cucina") non dice cosa si sta comprando né quanto.
 */
export const itemSummary = (chosen: ItemVariant | undefined, qty: number): string => {
  if (!chosen) return '';
  return qty > 1 ? `${chosen.name} × ${qty}` : chosen.name;
};

/**
 * La quantità VERA di un extra, definita in un posto solo: la usano sia il
 * prezzo mostrato sia il checkout, così non possono divergere.
 * - vino: un addebito per BOTTIGLIA;
 * - confezioni: il numero di confezioni scelte;
 * - articoli: le unità scelte (al massimo 10, come sul server);
 * - pulizia intermedia: un addebito per ogni giorno scelto;
 * - a persona / a notte: ospiti e notti della prenotazione, con la stessa
 *   regola del sito (ExtraServicesSelector.tsx);
 * - tutto il resto: la quantità scelta a mano, minimo 1.
 */
export function effectiveQuantity(
  item: AppExtra,
  selection: ExtraSelection,
  guests: number,
  nights: number,
): number {
  if (wineVariantsOf(item)) return Math.max(1, wineBottles(selection));
  if (bundleVariantsOf(item)) return Math.max(1, Number(selection.quantity) || 1);
  if (itemVariantsOf(item)) return Math.max(1, Math.min(itemMaxQuantity(selectedItem(item, selection)), Number(selection.quantity) || 1));
  if (item.options.cleaningDates) return Math.max(1, selection.cleaningDates?.length || 1);
  switch (item.priceType) {
    case 'per_person': return guests;
    case 'per_night': return nights;
    case 'per_person_night': return guests * nights;
    // La quantità scelta a mano, mai sopra il tetto: per la culla il tetto
    // sono i pezzi liberi per queste date, così l'anteprima non può mostrare
    // un totale che il server rifiuterà.
    default: return Math.max(1, Math.min(maxQuantityOf(item), Number(selection.quantity) || 1));
  }
}

/**
 * Le fasce del LISTINO, quelle su cui si legge il "da 5 €". Il channel
 * manager nuovo spegne (`isAvailable: false`) anche le fasce passate, a meno
 * di 30 minuti o prima che l'appartamento si liberi, col motivo in
 * `unavailableReason`: si comprano o no adesso (lo decide la scheda), ma il
 * prezzo resta quello. Fuori listino solo quelle spente dal gestore. Senza
 * motivo (channel manager vecchio) vale `isAvailable`, come prima.
 */
export function listedSlots(item: AppExtra): NonNullable<AppExtra['options']['timeSlotOptions']> {
  return (item.options.timeSlotOptions || []).filter((s) =>
    s.isAvailable || (!!s.unavailableReason && s.unavailableReason !== 'unavailable' && s.unavailableReason !== 'unknown_slot'));
}

/** Il prezzo mostrato: il server lo ricalcola comunque, questo è indicativo. */
/** La fascia oraria del listino più economica (null se nessuna ha un prezzo). */
export function cheapestSlotPrice(item: AppExtra): number | null {
  let min: number | null = null;
  listedSlots(item).forEach((s) => {
    if (s.price > 0 && (min === null || s.price < min)) min = s.price;
  });
  return min;
}

export function previewPrice(
  item: AppExtra,
  selection: ExtraSelection,
  guests: number,
  nights: number,
): number {
  // Transfer: il prezzo non è "il prezzo del servizio" ma la somma delle
  // tratte scelte (più il supplemento notturno). Stessa regola del sito,
  // in src/lib/transferPricing.ts.
  if (item.options.transfer) {
    return quoteTransfer(item.serviceId, item.options.transfer, selection as TransferSelection).total;
  }
  // Vino: il prezzo NON è quello del servizio (a listino è 0) ma la somma
  // delle bottiglie scelte, sconto per-variante incluso. Stessa regola del
  // server, che ignora del tutto ospiti e notti per il vino.
  const wines = wineVariantsOf(item);
  if (wines) {
    let sum = 0;
    wines.forEach((v) => { sum += wineUnitPrice(v) * wineQtyOf(selection, v.id); });
    return Math.round(sum * 100) / 100;
  }
  // Confezioni: vale il prezzo del bundle per il numero di confezioni, senza
  // moltiplicatori di soggiorno — è la regola del server, che tratta il
  // prezzo del bundle come un "unitPrice già deciso" (extra-pricing.ts).
  const bundles = bundleVariantsOf(item);
  if (bundles) {
    const chosen = selectedBundle(item, selection);
    if (!chosen) return 0;
    const packs = Math.max(1, Number(selection.quantity) || 1);
    return Math.round(chosen.price * packs * 100) / 100;
  }
  // Articoli: vale il prezzo dell'articolo scelto per le unità, senza
  // moltiplicatori di soggiorno — il servizio a listino costa 0, il prezzo
  // sta tutto nell'articolo. Senza un articolo scelto non c'è un prezzo:
  // il "da 2,00 €" delle schede lo dà groupStartingPrice.
  const itemVariants = itemVariantsOf(item);
  if (itemVariants) {
    const chosen = selectedItem(item, selection);
    if (!chosen) return 0;
    const units = Math.max(1, Math.min(itemMaxQuantity(chosen), Number(selection.quantity) || 1));
    return Math.round(itemUnitPrice(chosen) * units * 100) / 100;
  }
  // Fascia oraria: vale il prezzo della fascia, senza moltiplicatori. Senza
  // una fascia scelta, l'anteprima è la più economica fra quelle disponibili
  // (il "da 15 €" della scheda), non il listino nudo del servizio.
  if (item.options.timeSlotOptions && item.options.timeSlotOptions.length > 0) {
    const slot = selection.selectedTime
      ? item.options.timeSlotOptions.filter((s) => s.time === selection.selectedTime)[0]
      : undefined;
    if (slot) return slot.price;
    const cheapest = cheapestSlotPrice(item);
    if (!selection.selectedTime && cheapest !== null) return cheapest;
  }
  const qty = effectiveQuantity(item, selection, guests, nights);
  if (selection.cleaningVariant && item.options.cleaningVariants) {
    const variant = item.options.cleaningVariants.filter((v) => v.id === selection.cleaningVariant)[0];
    if (variant) return variant.price * qty;
  }
  return item.price * qty;
}

/**
 * Servizi che il catalogo espone ma che l'app non può vendere: quelli a
 * prezzo zero che non sono "su richiesta" e non hanno nessuna opzione con
 * un prezzo (fasce, varianti di pulizia, bottiglie, confezioni). Un
 * "Acquista" lì finirebbe sempre in "Importo non valido": meglio dire dove il
 * servizio si ottiene davvero.
 */
export function notSellableInApp(item: AppExtra): boolean {
  if (item.priceType === 'on_request') return false;
  // Il transfer SI compra in app: il prezzo a listino è 0 perché sta tutto
  // nelle tratte, e il form sa comporlo.
  if (item.options.transfer) return false;
  const pricedOptions =
    (item.options.timeSlotOptions || []).some((s) => s.price > 0) ||
    (item.options.cleaningVariants || []).some((v) => v.price > 0) ||
    (item.options.bundleVariants || []).some((b) => b.price > 0) ||
    (item.options.itemVariants || []).some((v) => v.price > 0) ||
    // Una bottiglia esaurita non è un prezzo che si può incassare.
    (item.options.wineVariants || []).some((v) => v.isAvailable && v.price > 0);
  return !(item.price > 0) && !pricedOptions;
}

/** Il bottone si accende? (le stesse condizioni che il server rivalida) */
export function canBuyExtra(item: AppExtra, selection: ExtraSelection, limits?: TransferDateLimits): boolean {
  // Transfer: il bottone (acquisto O richiesta) si accende quando il form è
  // completo — tratta scelta, orario, volo se richiesto, stazione a listino.
  const transfer = item.options.transfer;
  if (transfer) {
    if (!item.available && !item.canRequest) return false;
    const sel = selection as TransferSelection;
    // Una tratta di arrivo che in questa fase non esiste più non è "form
    // completo": il server risponderebbe `arrival_not_available`.
    if (sel.isArrival && transfer.arrivalAllowed === false) return false;
    return validateTransferSelection(item.serviceId, transfer, sel, limits) === null;
  }
  if (!item.available) return false;
  if (notSellableInApp(item)) return false;
  // Vino: senza nemmeno una bottiglia non c'è niente da pagare (il server
  // risponderebbe `wine_no_bottles`).
  if (wineVariantsOf(item) && wineBottles(selection) === 0) return false;
  // Confezioni: senza sceglierne una il prezzo non esiste (il server
  // risponderebbe `bundle_not_selected`).
  if (bundleVariantsOf(item) && !selectedBundle(item, selection)) return false;
  // Articoli: idem, il server risponderebbe `item_not_selected`.
  if (itemVariantsOf(item) && !selectedItem(item, selection)) return false;
  if (item.options.requiresTimeSelection && !selection.selectedTime) return false;
  // Una fascia spenta non si paga: per il check-in anticipato il catalogo
  // spegne anche quelle passate, a meno di 30 minuti o prima che
  // l'appartamento si liberi (il server risponderebbe
  // `early_checkin_unavailable`).
  if (selection.selectedTime
    && (item.options.timeSlotOptions || []).some((s) => s.time === selection.selectedTime && !s.isAvailable)) {
    return false;
  }
  if (item.serviceId === 'extra_cleaning') {
    if (!selection.cleaningDates || selection.cleaningDates.length === 0) return false;
    if (item.options.cleaningVariants && item.options.cleaningVariants.length > 0 && !selection.cleaningVariant) {
      return false;
    }
  }
  return true;
}

/**
 * Comprare o chiedere? Entro il termine (`minAdvanceDays`, calcolato dal
 * server per ciascuna tratta) si compra; oltre — o se la tratta scelta è
 * quella già scaduta — si può ancora inviare una richiesta, che il gestore
 * approva e trasforma in un link di pagamento.
 */
export function transferAction(
  item: AppExtra,
  selection: ExtraSelection,
): 'buy' | 'request' | null {
  const transfer = item.options.transfer;
  if (!transfer) return null;
  if (!item.available) return 'request';
  const sel = selection as TransferSelection;
  // Nessuna tratta ancora scelta: "fuori termine" solo se lo sono TUTTE
  // quelle prenotabili. Prima un modulo vuoto valeva come richiesta e
  // l'ospite leggeva "termine scaduto" su un transfer ancora acquistabile
  // (segnalazione set 2026: pagato poi regolarmente dal carrello).
  if (!sel.isArrival && !sel.isDeparture) {
    const arrivalOpen = transfer.arrivalAllowed !== false && transfer.arrivalOpen;
    return arrivalOpen || transfer.departureOpen ? 'buy' : 'request';
  }
  return canBuyTransfer(transfer, sel) ? 'buy' : 'request';
}

/** Il transfer ha almeno una tratta scelta? (senza, il bottone chiede di sceglierla) */
export function transferLegChosen(selection: ExtraSelection): boolean {
  const sel = selection as TransferSelection;
  return !!sel.isArrival || !!sel.isDeparture;
}

/** I dettagli del transfer nel formato che salva il sito. */
export const CUSTOM_TAXI_ID = 'custom_transfer';

/** Il taxi su misura si puo' chiedere solo con meta, giorno, ora e persone. */
export function customTaxiComplete(selection: ExtraSelection): boolean {
  return String(selection.destination || '').trim().length >= 2
    && /^\d{4}-\d{2}-\d{2}$/.test(String(selection.transferDate || ''))
    && /^\d{2}:\d{2}$/.test(String(selection.transferTime || ''))
    && (Number(selection.people) || 2) >= 1;
}

export function customTaxiDetailsOf(selection: ExtraSelection): ExtraCheckoutDetails {
  return {
    destination: String(selection.destination || '').trim(),
    transferDate: selection.transferDate,
    transferTime: selection.transferTime,
    people: Math.max(1, Number(selection.people) || 2),
    roundTrip: !!selection.roundTrip,
    returnTime: selection.roundTrip ? selection.returnTime : undefined,
    notes: selection.notes,
  };
}

export function transferDetailsOf(selection: ExtraSelection): ExtraCheckoutDetails {
  return {
    isArrival: !!selection.isArrival,
    isDeparture: !!selection.isDeparture,
    arrivalDateTime: selection.arrivalDateTime,
    departureDateTime: selection.departureDateTime,
    arrivalTime: selection.arrivalTime,
    departureTime: selection.departureTime,
    stationName: selection.stationName,
    departureStationName: selection.departureStationName,
    flightNumber: selection.flightNumber,
    departureFlightNumber: selection.departureFlightNumber,
    notes: selection.notes,
  };
}

/** La scorta fisica da mostrare (null quando il servizio non ne ha). */
export function stockOf(item: AppExtra): number | null {
  const opts = item.options;
  return typeof opts.inventoryAvailable === 'number' && Number(opts.inventoryTotal) > 0
    ? opts.inventoryAvailable
    : null;
}

/**
 * Il TETTO dello stepper della quantità.
 *
 * Per i servizi normali è il tetto generico (10, lo stesso del server). Per
 * un servizio a SCORTA FISICA — la culla — non è un numero deciso qui: sono
 * i pezzi ancora liberi per LE DATE DI QUESTO SOGGIORNO, calcolati dal
 * server (`inventoryAvailable`). Chiedere la terza culla quando ne restano
 * due non è una scelta che l'app debba lasciar fare per poi farla rimbalzare
 * al pagamento: il "+" si spegne prima.
 *
 * Il server rivalida comunque al checkout (`inventory_insufficient`): questo
 * catalogo può avere qualche minuto, la scorta no.
 */
export function maxQuantityOf(item: AppExtra): number {
  const stock = stockOf(item);
  // Magazzino dei prodotti che si consumano (tarallini & co.): il tetto sono
  // i pezzi rimasti, se il gestore li conta.
  const consumable = typeof item.options.stockLeft === 'number' ? item.options.stockLeft : null;
  const caps = [MAX_ITEM_QUANTITY];
  if (stock !== null) caps.push(stock);
  if (consumable !== null) caps.push(consumable);
  return Math.max(1, Math.min(...caps));
}

/** Il tetto delle unità di un articolo: 10, o i pezzi rimasti se contati. */
export function itemMaxQuantity(v: Pick<ItemVariant, 'stockLeft'> | undefined): number {
  const left = v && typeof v.stockLeft === 'number' ? v.stockLeft : null;
  return left === null ? MAX_ITEM_QUANTITY : Math.max(1, Math.min(MAX_ITEM_QUANTITY, left));
}

/** "Ultimi N": i pezzi rimasti da dire all'ospite, solo quando sono pochi. */
export const LOW_STOCK_SHOWN = 3;
export function lowStockLeft(v: { stockLeft?: number; soldOut?: boolean } | undefined): number | null {
  if (!v || v.soldOut || typeof v.stockLeft !== 'number') return null;
  return v.stockLeft > 0 && v.stockLeft <= LOW_STOCK_SHOWN ? v.stockLeft : null;
}

/**
 * I pezzi di un servizio a scorta già acquistati da QUESTA prenotazione.
 * Le righe scritte prima della quantità non hanno il campo: valgono un pezzo,
 * esattamente come le conta il channel manager.
 */
export function purchasedStockUnits(
  purchased: PurchasedExtra[] | undefined,
  serviceId: string,
): number {
  let units = 0;
  (purchased || []).forEach((e) => {
    if (e.serviceId === serviceId) units += Math.max(1, Math.trunc(Number(e.quantity) || 1));
  });
  return units;
}

/** Le fasi in cui chiedere un servizio a scorta fuori termine ha ancora senso. */
const STOCK_REQUEST_PHASES: StayPhase[] = ['before_checkin', 'in_stay'];

/**
 * LE DUE REGOLE DEI SERVIZI A SCORTA (oggi: la culla), applicate al catalogo
 * appena arrivato e in un posto solo, così le tre schermate degli extra non
 * possano raccontarne versioni diverse.
 *
 * 1. TETTO INCREMENTALE invece del blocco "già acquistata". Il server conta
 *    come liberi anche i pezzi già intestati a questa prenotazione (li
 *    esclude apposta dal conteggio, altrimenti chi ne ha comprato uno non
 *    vedrebbe mai il margine rimasto): qui si sottraggono i pezzi già suoi,
 *    così lo stepper si ferma dove si ferma davvero la scorta e una seconda
 *    culla si può aggiungere solo se ce n'è una libera. Finiti i pezzi la
 *    card torna a dire "già acquistata", che è la verità per chi li ha presi
 *    tutti lui.
 * 2. RICHIESTA FUORI TERMINE SOLO QUANDO HA SENSO. Oltre il termine d'ordine
 *    il server riaccende `canRequest` guardando solo la scorta, mai la fase
 *    del soggiorno: il giorno della partenza l'app finiva per proporre
 *    "Invia una richiesta" per una culla da montare in una casa che si sta
 *    lasciando, cioè una richiesta nata per essere rifiutata. Prima
 *    dell'arrivo e durante il soggiorno la richiesta resta.
 *
 * Tocca SOLO i servizi con una scorta (`inventoryTotal > 0`): vino, pulizie
 * extra, transfer e articoli a pezzo passano di qui senza una modifica.
 */
export function applyStockRules(catalog: ExtrasCatalog): ExtrasCatalog {
  const phase = catalog.stayPhase;
  let changed = false;
  const items = (catalog.items || []).map((item) => {
    if (!(Number(item.options.inventoryTotal) > 0)) return item;
    const owned = purchasedStockUnits(catalog.purchasedExtras, item.serviceId);
    const free = Math.max(0, (Number(item.options.inventoryAvailable) || 0) - owned);
    const next: AppExtra = { ...item, options: { ...item.options, inventoryAvailable: free } };
    if (free <= 0 && owned > 0) {
      next.available = false;
      next.alreadyPurchased = true;
      // Chi ha preso tutti i pezzi liberi deve leggere "già acquistata", non
      // un "Esaurita per le tue date" in rosso: il servizio ce l'ha lui.
      next.reason = 'sold_out';
    }
    if (!next.available && next.canRequest && phase && STOCK_REQUEST_PHASES.indexOf(phase) < 0) {
      next.canRequest = false;
      // Senza il bottone, "puoi comunque farne richiesta" sarebbe una bugia:
      // il motivo torna a essere la fase del soggiorno (a chi ce l'ha già
      // resta il suo "già acquistata").
      if (!next.alreadyPurchased) next.reason = 'wrong_phase';
    }
    changed = true;
    return next;
  });
  return changed ? { ...catalog, items } : catalog;
}

/** Quello che serve ad aprire il foglio di pagamento per questo servizio. */
export interface PaySheetPayload {
  serviceId: string;
  serviceName: string;
  price: number;
  quantity: number;
  details: ExtraCheckoutDetails;
  /** Cosa si sta comprando davvero, quando il nome non basta. */
  subtitle?: string;
}

/**
 * Il carico del foglio di pagamento, costruito una volta sola: la lista e le
 * schermate dedicate devono mandare al server ESATTAMENTE la stessa cosa.
 */
export function buildPaySheetPayload(
  item: AppExtra,
  selection: ExtraSelection,
  guests: number,
  nights: number,
  bundleLabels: BundleLabels,
): PaySheetPayload {
  if (item.options.transfer) {
    return {
      serviceId: item.serviceId,
      serviceName: item.name,
      price: previewPrice(item, selection, guests, nights),
      // Le tratte le conta il server dal contenuto di `details`: mandare
      // una quantità da qui la farebbe moltiplicare due volte.
      quantity: 1,
      details: transferDetailsOf(selection),
    };
  }

  const wines = wineVariantsOf(item);
  const bundles = bundleVariantsOf(item);
  const items = itemVariantsOf(item);
  const packs = Math.max(1, Number(selection.quantity) || 1);
  // Il Cestino: la bottiglia scelta (o la predefinita) va al server e nel
  // riepilogo ("Il Cestino della Nonna · Prosecco"); il prezzo non cambia.
  const drink = items ? chosenKitDrink(selectedItem(item, selection), selection) : undefined;
  const itemText = items ? itemSummary(selectedItem(item, selection), effectiveQuantity(item, selection, guests, nights)) : '';

  return {
    serviceId: item.serviceId,
    serviceName: item.name,
    // Il vino e le confezioni portano il loro elenco in testata: il nome del
    // servizio da solo non dice cosa si sta pagando.
    subtitle: wines
      ? wineSummary(wines, selection)
      : bundles
        ? bundleSummary(selectedBundle(item, selection), bundleLabels, packs)
        : items
          ? (drink ? `${itemText} · ${drink.name}` : itemText)
          : undefined,
    price: previewPrice(item, selection, guests, nights),
    // Stessa quantità dell'anteprima: il prezzo visto è il prezzo pagato.
    // Per i prezzi a persona / a notte si manda 1: ospiti e notti li
    // moltiplica il server dalla prenotazione, e mandarli anche da qui li
    // farebbe contare due volte.
    quantity: isStayPriced(item.priceType) ? 1 : effectiveQuantity(item, selection, guests, nights),
    details: {
      selectedTime: selection.selectedTime,
      cleaningVariant: selection.cleaningVariant,
      cleaningDates: selection.cleaningDates,
      // Le bottiglie nello STESSO formato del tablet ([{ id, qty }]): il
      // server le rivalida una per una e ricompone il prezzo da solo.
      wineSelections: wines ? selection.wineSelections : undefined,
      // Idem per la confezione: si manda l'id, il prezzo lo fa il listino.
      bundleVariant: bundles ? selection.bundleVariant : undefined,
      // Idem per l'articolo: si manda l'id e le unità stanno in `quantity`.
      itemVariant: items ? selection.itemVariant : undefined,
      kitDrink: drink ? drink.id : undefined,
      notes: selection.notes,
    },
  };
}

/** Il risultato di una quantità riportata entro la scorta rimasta. */
export interface StockClamp {
  /** Le righe da rimandare al server, con quantità e prezzo aggiornati. */
  rows: PaySheetPayload[];
  /** I pezzi dell'ultima riga abbassata (0 = non c'era niente da abbassare). */
  units: number;
  /** Il totale da pagare dopo l'abbassamento. */
  total: number;
  changed: boolean;
}

/**
 * Le righe riportate entro i pezzi che il server ha appena dichiarato liberi.
 *
 * Quando una riga chiede più pezzi di quanti ne restano il server rifiuta
 * TUTTO il pagamento (`inventory_insufficient`): ripetere la stessa quantità
 * è un ciclo che fallirà sempre. Qui la riga si abbassa al numero dichiarato
 * e il prezzo si rifà dal prezzo unitario della riga stessa, così la testata
 * del foglio mostra quello che si sta per pagare davvero — il server riprezza
 * comunque da capo.
 */
export function clampToStock(
  rows: PaySheetPayload[],
  /** serviceId (o "serviceId:articolo") → pezzi ancora liberi, come li ha detti il server. */
  caps: Record<string, number>,
  /** Il totale mostrato prima dell'abbassamento (il carrello intero). */
  baseTotal: number,
): StockClamp {
  let removed = 0;
  let units = 0;
  let changed = false;
  const out = rows.map((row) => {
    // Con più righe dello stesso servizio (spazzolino e dentifricio) il
    // server dice anche QUALE articolo: vale il tetto di quello.
    const item = row.details && row.details.itemVariant;
    const byItem = item ? caps[`${row.serviceId}:${item}`] : undefined;
    const cap = typeof byItem === 'number' ? byItem : caps[row.serviceId];
    const quantity = Number(row.quantity) || 0;
    if (typeof cap !== 'number' || !(cap > 0) || !(quantity > cap)) return row;
    const unit = quantity > 0 ? (Number(row.price) || 0) / quantity : 0;
    const price = Math.round(unit * cap * 100) / 100;
    removed += (Number(row.price) || 0) - price;
    units = cap;
    changed = true;
    return { ...row, quantity: cap, price };
  });
  return {
    rows: out,
    units,
    changed,
    total: Math.max(0, Math.round((baseTotal - removed) * 100) / 100),
  };
}

// ------------------------------------------------------------------
// Ordine minimo dei PRODOTTI consegnati (channel manager nuovo)
// ------------------------------------------------------------------

/** Quanto manca all'ordine minimo: null = a posto (o nessun minimo). */
export interface MinOrderShortfall {
  minOrder: number;
  productsTotal: number;
  missing: number;
}

/**
 * Le righe (del carrello o della scheda) che sono PRODOTTI consegnati
 * sommate contro il minimo del catalogo. I servizi non contano; un ordine
 * di soli prodotti gratuiti, o senza prodotti, è sempre a posto. Il server
 * fa lo stesso conto (lib/extra-min-order del channel manager).
 */
export function minOrderShortfall(
  rows: Array<Pick<PaySheetPayload, 'serviceId' | 'price'>>,
  items: AppExtra[],
  minOrder: number | undefined,
): MinOrderShortfall | null {
  const min = Math.round((Number(minOrder) || 0) * 100) / 100;
  if (!(min > 0)) return null;
  const products = new Set(items.filter((i) => i.deliveredProduct).map((i) => i.serviceId));
  let total = 0;
  rows.forEach((r) => { if (products.has(r.serviceId)) total += Number(r.price) || 0; });
  const productsTotal = Math.round(total * 100) / 100;
  if (!(productsTotal > 0) || productsTotal + 0.001 >= min) return null;
  return { minOrder: min, productsTotal, missing: Math.round((min - productsTotal) * 100) / 100 };
}

/** "5 €" per le cifre tonde, "4,25 €" per le altre (Intl, lingua dell'app). */
export function formatEuroShort(amount: number, locale: string): string {
  const whole = Math.abs(amount - Math.round(amount)) < 0.005;
  try {
    return new Intl.NumberFormat(locale || 'it', {
      style: 'currency', currency: 'EUR', minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2,
    }).format(amount);
  } catch {
    return `${whole ? Math.round(amount) : amount.toFixed(2)} €`;
  }
}

/** "Ordine minimo per i prodotti: 5 € — aggiungi ancora 4,25 €." */
export function minOrderText(
  t: (key: string, options?: Record<string, unknown>) => string,
  s: Pick<MinOrderShortfall, 'minOrder' | 'missing'>,
  locale: string,
): string {
  return t('extras.minOrder.message', { min: formatEuroShort(s.minOrder, locale), missing: formatEuroShort(s.missing, locale) });
}

